/**
 * The qbo_connect result banner (F11) — the reader half of the capture
 * in src/lib/qboConnectSignal.ts. The token rides sessionStorage from the
 * callback's redirect to the app root, so this renders on whatever
 * surface mounts it rather than where the redirect landed.
 *
 * TWO SURFACES mount it: the QuickBooks settings, where Connect is
 * pressed, and the Invoices rail, where a billing operator works. They
 * are different routes, so only one can render a given token, and the
 * first to show it spends it (see the claim effect below). Any third
 * mount must be able to satisfy the copy: two failure sentences tell the
 * operator to check the connection status "on this page", so a surface
 * that shows no connection status must not mount this.
 *
 * Three shapes:
 *  - failure tokens render the ruled sentence (role="alert");
 *  - 'another_company_connected' (the second-company refusal) upgrades
 *    its sentence with the name of the company that IS connected, via
 *    the same status read the card already makes (react-query dedupes
 *    the two into one request);
 *  - 'connected' renders a CALM success banner naming the company.
 *
 * The status read is decoration on a fact: while it loads (or fails)
 * the banner shows the honest nameless sentence, never a blank.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { useTenant } from '@/lib/auth';
import { useSupportContact } from '@/hooks/useSupportContact';
import { qboApi, type QboConnectionWire } from '@/lib/qboApi';
import {
  clearQboConnectToken,
  qboAnotherCompanySentence,
  qboConnectFailureSentence,
  readQboConnectToken,
} from '@/lib/qboConnectSignal';

/** The connected (or expired) company's stored name and id, from the
 *  status wire; nulls while loading, absent, or multiple. */
function useConnectedCompany(): { name: string | null; realmId: string | null } {
  const { tenantSlug } = useTenant();
  // The SAME queryKey the QuickBooks card and the rail's connection
  // summary read, deliberately: surfaces sharing a page share one
  // request.
  const statusQuery = useQuery({
    queryKey: ['qbo', 'status', tenantSlug],
    queryFn: ({ signal }) => qboApi.status(tenantSlug, signal),
  });
  const connection: QboConnectionWire | undefined = statusQuery.data?.connection;
  if (connection === undefined || connection.status === 'absent' || connection.status === 'multiple') {
    return { name: null, realmId: null };
  }
  return { name: connection.company_name ?? null, realmId: connection.realmId };
}

function DismissButton({ testId, onDismiss }: { testId: string; onDismiss: () => void }): ReactNode {
  return (
    <Button variant="ghost" size="sm" data-testid={testId} onClick={onDismiss}>
      Dismiss
    </Button>
  );
}

/** The second-company refusal, with the standing company named when the
 *  status read holds a name (qboAnotherCompanySentence falls back to
 *  the honest nameless sentence otherwise). */
function AnotherCompanyBanner({ onDismiss }: { onDismiss: () => void }): ReactNode {
  const { name } = useConnectedCompany();
  return (
    <div
      data-testid="qbo-connect-banner"
      role="alert"
      className="mb-3 flex items-start justify-between gap-3 border border-pepper/30 bg-pepper-soft rounded-tight p-3"
    >
      <p className="text-sm text-pepper-deep">{qboAnotherCompanySentence(name)}</p>
      <DismissButton testId="qbo-connect-banner-dismiss" onDismiss={onDismiss} />
    </div>
  );
}

/** The 'connected' success banner: calm, dismissible, and it names the
 *  company (falling back to "company {realm_id}", then to plain
 *  QuickBooks while the status read is still in flight). role="status",
 *  never "alert": a success is not an interruption. */
function ConnectedBanner({ onDismiss }: { onDismiss: () => void }): ReactNode {
  const { name, realmId } = useConnectedCompany();
  const who = name ?? (realmId !== null ? `company ${realmId}` : null);
  return (
    <div
      data-testid="qbo-connect-success-banner"
      role="status"
      className="mb-3 flex items-start justify-between gap-3 border border-sage-200 bg-sage-50 rounded-tight p-3"
    >
      <p className="text-sm text-ink">
        {who === null ? 'Connected to QuickBooks.' : `Connected to ${who} in QuickBooks.`}
      </p>
      <DismissButton testId="qbo-connect-success-dismiss" onDismiss={onDismiss} />
    </div>
  );
}

export function QboConnectResultBanner(): ReactNode {
  const contact = useSupportContact();
  // A pure read. This runs during render, and React double-invokes state
  // initializers to check they have no side effects, so the claim below
  // cannot live here.
  const [token, setToken] = useState<string | null>(() => readQboConnectToken());

  // ONE TOKEN, ONE REPORT. This banner is mounted on more than one
  // surface, so the first to display a token must spend it. Otherwise the
  // outcome of a single connect attempt surfaces again later in the same
  // browser session, on another page, reading as though it had just
  // happened. Dismiss is how an operator closes a message, not what stops
  // it returning: someone who reads it and navigates away has been told.
  //
  // The state above holds what was claimed, so the message survives every
  // re-render of the surface showing it, and survives StrictMode's replay
  // of this effect (clearing twice is clearing once).
  useEffect(() => {
    if (token != null) clearQboConnectToken();
  }, [token]);

  if (token == null) return null;

  const dismiss = (): void => {
    clearQboConnectToken();
    setToken(null);
  };

  if (token === 'connected') return <ConnectedBanner onDismiss={dismiss} />;
  if (token === 'another_company_connected') return <AnotherCompanyBanner onDismiss={dismiss} />;

  return (
    <div
      data-testid="qbo-connect-banner"
      role="alert"
      className="mb-3 flex items-start justify-between gap-3 border border-pepper/30 bg-pepper-soft rounded-tight p-3"
    >
      <p className="text-sm text-pepper-deep">{qboConnectFailureSentence(token, contact)}</p>
      <DismissButton testId="qbo-connect-banner-dismiss" onDismiss={dismiss} />
    </div>
  );
}
